import type { FC, ReactNode } from 'react'
import type { CurrencyConfig, Settings } from '@/types'

import { createContext, useContext, useMemo } from 'react'
import { DEFAULT_SETTINGS } from '@/constants/inventory'

import {
    formatMoney,
    formatNumber,
    formatPercent,
    formatSigned,
    getCurrency
} from '@/lib/format'

type SettingsContextValue = {
    settings: Settings
    currency: CurrencyConfig
    money: (value: number) => string
    number: (value: number) => string
    signed: (value: number) => string
    percent: (value: number) => string
}

interface SettingsProviderProps {
    settings?: Settings | null
    children: ReactNode
}

const SettingsContext = createContext<SettingsContextValue | null>(null)

export const SettingsProvider: FC<SettingsProviderProps> = ({ settings, children }): ReactNode => {
    const value = useMemo<SettingsContextValue>(() => {
        const resolved = settings ?? DEFAULT_SETTINGS
        const currency = getCurrency(resolved.currency)

        return {
            settings: resolved,
            currency,
            money: (amount: number) => formatMoney(amount, currency),
            number: (amount: number) => formatNumber(amount, currency),
            signed: (amount: number) => formatSigned(amount, currency),
            percent: (amount: number) => formatPercent(amount, currency)
        }
    }, [settings])

    return (
        <SettingsContext.Provider value={value}>
            {children}
        </SettingsContext.Provider>
    )
}

export const useSettings = (): SettingsContextValue => {
    const context = useContext(SettingsContext)

    if (!context) throw new Error('useSettings must be used inside SettingsProvider')

    return context
}
